import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TokenService implements OnDestroy {
  private readonly refreshTokenKey = 'refreshToken';
  private readonly loginRefreshTokenKey = 'loginRefreshToken';

  private readonly isRefreshTokenValidSubject = new BehaviorSubject<boolean>(
    this.isRefreshTokenValid()
  );
  isRefreshTokenValid$ = this.isRefreshTokenValidSubject.asObservable();

  private readonly isEveportalJwtTokenValidSubject =
    new BehaviorSubject<boolean>(this.isEveportalJwtTokenValid());
  isEveportalJwtTokenValid$ =
    this.isEveportalJwtTokenValidSubject.asObservable();
  eveUserSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    this.getLoginRefreshToken()?.eveUser
  );
  eveUser$ = this.eveUserSubject.asObservable();

  private refreshTokenTimer?: any;
  private eveportalTokenTimer?: any;
  private eveportalTokenCheckSubscription?: Subscription;

  constructor() {
    // Schedule timers for existing tokens
    this.scheduleRefreshTokenCheck();
    this.scheduleEveportalTokenCheck();
  }

  ngOnDestroy(): void {
    this.clearTimers();
    this.eveportalTokenCheckSubscription?.unsubscribe();
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  getEveportalJwtToken(): string | null {
    const loginResult = this.getLoginRefreshToken();
    if (!!loginResult) {
      return loginResult.jwt;
    }
    return null;
  }

  getLoginRefreshToken(): any {
    const loginResultString = localStorage.getItem(this.loginRefreshTokenKey);
    if (!!loginResultString) {
      return JSON.parse(loginResultString);
    } else {
      return null;
    }
  }

  setRefreshToken(token: string): void {
    try {
      const isValid = token !== null && !this.isTokenExpired(token);

      if (isValid) {
        localStorage.setItem(this.refreshTokenKey, token);
        this.isRefreshTokenValidSubject.next(true);
        this.scheduleRefreshTokenCheck(); // Schedule check at expiration
      } else {
        console.warn('⚠️ Attempted to set invalid/expired refresh token');
        this.clearRefreshToken();
        this.isRefreshTokenValidSubject.next(false);
      }
    } catch (error) {
      console.error('❌ Failed to set refresh token:', error);
      this.isRefreshTokenValidSubject.next(false);
    }
  }

  setLoginRefreshToken(loginResult: any): void {
    try {
      const jwtToken = loginResult?.jwt;
      const isValid = jwtToken && !this.isTokenExpired(jwtToken);

      if (isValid) {
        localStorage.setItem(
          this.loginRefreshTokenKey,
          JSON.stringify(loginResult)
        );
        this.isEveportalJwtTokenValidSubject.next(true);
        this.eveUserSubject.next(loginResult.eveUser);
        this.scheduleEveportalTokenCheck(); // Schedule check at expiration
      } else {
        console.warn('⚠️ Attempted to set invalid/expired login token');
        localStorage.removeItem(this.loginRefreshTokenKey);
        this.isEveportalJwtTokenValidSubject.next(false);
        this.eveUserSubject.next(null);
      }
    } catch (error) {
      console.error('❌ Failed to set login refresh token:', error);
      this.isEveportalJwtTokenValidSubject.next(false);
      this.eveUserSubject.next(null);
    }
  }

  clearRefreshToken(): void {
    localStorage.removeItem(this.refreshTokenKey);
  }

  clearAllTokens(): void {
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.loginRefreshTokenKey);

    this.isRefreshTokenValidSubject.next(false);
    this.isEveportalJwtTokenValidSubject.next(false);

    this.clearTimers();
  }

  isRefreshTokenValid(): boolean {
    const token = this.getRefreshToken();
    return token !== null && !this.isTokenExpired(token);
  }

  isEveportalJwtTokenValid(): boolean {
    const token = this.getEveportalJwtToken();
    return token !== null && !this.isTokenExpired(token);
  }

  getJwtTokenPayload(): any {
    const token = this.getEveportalJwtToken();
    if (!token) return null;

    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (error) {
      console.warn('❌ Failed to decode JWT token payload:', error);
      return null;
    }
  }

  // === Token Validation ===

  private isTokenExpired(token: string | null): boolean {
    if (!token) return true;
    const expiry = this.getTokenExpiration(token);
    if (!expiry) return true;

    const now = Math.floor(Date.now() / 1000);
    return expiry <= now;
  }

  // === Helper to decode the token ===

  private getTokenExpiration(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp;
    } catch (error) {
      console.warn('❌ Failed to decode token expiration:', error);
      return null;
    }
  }

  private scheduleRefreshTokenCheck(): void {
    // Clear existing timer
    if (this.refreshTokenTimer) {
      clearTimeout(this.refreshTokenTimer);
    }

    const token = this.getRefreshToken();
    if (!token) return;

    const expiry = this.getTokenExpiration(token);
    if (!expiry) return;

    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = (expiry - now) * 1000; // Convert to milliseconds

    if (timeUntilExpiry > 0) {
      // Schedule check right after expiration
      this.refreshTokenTimer = setTimeout(() => {
        this.checkRefreshTokenValidity();
      }, timeUntilExpiry + 1000); // +1 second to ensure it's expired
    } else {
      // Already expired
      this.isRefreshTokenValidSubject.next(false);
    }
  }

  private scheduleEveportalTokenCheck(): void {
    // Clear existing timer
    if (this.eveportalTokenTimer) {
      clearTimeout(this.eveportalTokenTimer);
    }

    const token = this.getEveportalJwtToken();
    if (!token) return;

    const expiry = this.getTokenExpiration(token);
    if (!expiry) return;

    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = (expiry - now) * 1000;

    if (timeUntilExpiry > 0) {
      // Schedule check right after expiration
      this.eveportalTokenTimer = setTimeout(() => {
        this.checkEveportalTokenValidity();
      }, timeUntilExpiry + 1000);
    } else {
      // Already expired
      this.isEveportalJwtTokenValidSubject.next(false);
    }
  }

  private checkRefreshTokenValidity(): void {
    const isValid = this.isRefreshTokenValid();
    this.isRefreshTokenValidSubject.next(isValid);

    if (!isValid) {
      console.warn('🔒 Refresh token expired');
      this.clearRefreshToken();
    }
  }

  private checkEveportalTokenValidity(): void {
    const isValid = this.isEveportalJwtTokenValid();
    this.isEveportalJwtTokenValidSubject.next(isValid);

    if (!isValid) {
      console.warn('🔒 Eveportal JWT token expired');
      // Don't auto-clear, let the app handle re-authentication
    }
  }

  private clearTimers(): void {
    if (this.refreshTokenTimer) {
      clearTimeout(this.refreshTokenTimer);
      this.refreshTokenTimer = undefined;
    }
    if (this.eveportalTokenTimer) {
      clearTimeout(this.eveportalTokenTimer);
      this.eveportalTokenTimer = undefined;
    }
  }
}
